import {
    AVERAGE_CHARS_PER_FILE, FALLBACK_INWORLD_MODELS, FALLBACK_INWORLD_VOICES, INWORLD_DELIVERY_MODES,
    INWORLD_MAX_VOICES_PER_CELL, INWORLD_NIQQUD_MODES, INWORLD_SPEAKING_RATE, INWORLD_VOICE_ID_PATTERN, LANGUAGE_INFO,
    LANGUAGE_KEYS
} from '../../Mobx/AudioCatalog';

/**
 * The form of the Inworld settings screen ("הגדרות Inworld"). The fields of the form are strings, as the user typed them
 * (the voices of a language and gender are written in one field, separated by commas) - these functions convert the settings
 * of the server (GET /api/ai/settings/audio, the part "inworld") to the form, decide what has to be saved, and check the form
 * before it is sent (PATCH /api/ai/settings/audio/inworld). No React here, so they are easy to test.
 */

const VOICE_FIELDS = ['female', 'male'];

/** ['Ashley', 'Jessica'] from "Ashley, Jessica" (a voice id has no spaces, so commas and spaces both separate) */
export const parseVoices = (text) => String(text ?? '').split(/[\s,]+/).filter(Boolean);

const toList = (value) => {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') return parseVoices(value);
    return [];
};

const sameList = (a, b) => a.length === b.length && a.every((voice, index) => voice === b[index]);

/** The models that the form offers: the ones of the server, or the built-in ones for a server that does not send them */
export const inworldModelsOf = (inworldView) => (inworldView.catalog.models?.length ? inworldView.catalog.models : FALLBACK_INWORLD_MODELS);

/** The form shows what the automatic creation really uses (effective), not the raw saved values */
export const formFromInworldView = (inworldView) => {
    const { effective } = inworldView;
    const form = {
        model: effective.model,
        speakingRate: String(effective.speakingRate ?? INWORLD_SPEAKING_RATE.default),
        deliveryMode: effective.deliveryMode || INWORLD_DELIVERY_MODES[0],
        niqqud: effective.niqqud || INWORLD_NIQQUD_MODES[0],
    };
    for (const key of LANGUAGE_KEYS) {
        const language = effective.languages?.[key] || FALLBACK_INWORLD_VOICES[key] || {};
        form[key] = {};
        for (const field of VOICE_FIELDS) form[key][field] = toList(language[field]).join(', ');
    }
    return form;
};

/** A field is saved when the user changed it, or when the saved value is not valid (then it is replaced by the value of the form) */
const needsSave = (savedValue, effectiveValue, formValue) =>
    formValue !== effectiveValue || (savedValue !== undefined && savedValue !== effectiveValue);

const supportsDeliveryMode = (inworldView, modelId) => Boolean(inworldModelsOf(inworldView).find((model) => model.id === modelId)?.supportsDeliveryMode);

/**
 * What to send to the server: { model, speakingRate, deliveryMode, he: { female, male, niqqud }, en_US: { female }, ... }
 * Only the fields that have to be saved. {} - nothing to save.
 * A voice list with one voice is sent as a text and a longer one as a list (the shapes that the server saves).
 */
export const buildInworldPatch = (form, inworldView) => {
    const configured = inworldView.configured || {};
    const { effective } = inworldView;
    const patch = {};

    if (needsSave(configured.model, effective.model, form.model)) patch.model = form.model;

    const rate = parseSpeakingRate(form.speakingRate);
    if (needsSave(configured.speakingRate, effective.speakingRate, rate)) patch.speakingRate = rate;

    // the delivery mode is only for the models that support it - for the others it would be a field that nothing reads
    if (supportsDeliveryMode(inworldView, form.model) && needsSave(configured.deliveryMode, effective.deliveryMode, form.deliveryMode)) {
        patch.deliveryMode = form.deliveryMode;
    }

    for (const key of LANGUAGE_KEYS) {
        const saved = configured[key] || {};
        const current = effective.languages?.[key] || {};
        const changes = {};
        for (const field of VOICE_FIELDS) {
            const formList = parseVoices(form[key][field]);
            const currentList = toList(current[field]);
            const savedIsValid = saved[field] === undefined || sameList(toList(saved[field]), currentList);
            if (!sameList(formList, currentList) || !savedIsValid) {
                changes[field] = formList.length === 1 ? formList[0] : formList;
            }
        }
        if (key === 'he' && needsSave(saved.niqqud, effective.niqqud, form.niqqud)) changes.niqqud = form.niqqud;
        if (Object.keys(changes).length > 0) patch[key] = changes;
    }
    return patch;
};

/** The limits of the server for the voices of one language and gender (or the built-in ones for a server that does not send them) */
export const voiceLimitsOf = (inworldView) => {
    const limits = inworldView.catalog.limits || {};
    return {
        max: limits.voicesPerCell || INWORLD_MAX_VOICES_PER_CELL,
        pattern: limits.voiceIdPattern ? new RegExp(limits.voiceIdPattern) : INWORLD_VOICE_ID_PATTERN,
    };
};

/** The range of the speaking rate that the server accepts */
export const speakingRateRangeOf = (inworldView) => inworldView.catalog.limits?.speakingRate || INWORLD_SPEAKING_RATE;

/** The speaking rate as a number: NaN when the field is empty or is not a number (Number('') would be 0) */
export const parseSpeakingRate = (text) => (String(text ?? '').trim() === '' ? NaN : Number(text));

export const isSpeakingRateValid = (text, inworldView) => {
    const rate = parseSpeakingRate(text);
    const range = speakingRateRangeOf(inworldView);
    return Number.isFinite(rate) && rate >= range.min && rate <= range.max;
};

/** @returns {'' | 'count' | 'pattern'} what is wrong with the voices that are written in one field ('' - nothing) */
export const voiceFieldProblem = (text, inworldView) => {
    const { max, pattern } = voiceLimitsOf(inworldView);
    const voices = parseVoices(text);
    if (voices.length < 1 || voices.length > max) return 'count';
    if (voices.some((voice) => !pattern.test(voice))) return 'pattern';
    return '';
};

/** What a model costs to speak 1,000 files of a word or a short phrase (the list price, USD), or null when the price is not known */
export const estimateCostPerThousandFiles = (model, charsPerFile = AVERAGE_CHARS_PER_FILE) => {
    const price = model?.priceUsdPerMillionChars;
    return Number.isFinite(price) ? (price * charsPerFile) / 1000 : null;
};

/** @returns {string[]} what is wrong in the form (Hebrew). [] - the form can be saved. */
export const validateInworldForm = (form, view) => {
    const problems = [];
    const inworldView = view.inworld;
    const range = speakingRateRangeOf(inworldView);
    const { max: maxVoices } = voiceLimitsOf(inworldView);

    if (!inworldModelsOf(inworldView).some((model) => model.id === form.model)) problems.push('לא נבחר מודל.');

    if (!isSpeakingRateValid(form.speakingRate, inworldView)) {
        problems.push(`מהירות הדיבור חייבת להיות מספר בין ${range.min} ל-${range.max}.`);
    }

    if (supportsDeliveryMode(inworldView, form.model) && !INWORLD_DELIVERY_MODES.includes(form.deliveryMode)) {
        problems.push('מצב ההגשה אינו תקין.');
    }
    if (!INWORLD_NIQQUD_MODES.includes(form.niqqud)) problems.push('אפשרות הניקוד אינה תקינה.');

    for (const key of LANGUAGE_KEYS) {
        for (const field of VOICE_FIELDS) {
            const problem = voiceFieldProblem(form[key][field], inworldView);
            const where = `${LANGUAGE_INFO[key].label}, ${field === 'female' ? 'קול נשי' : 'קול גברי'}`;
            if (problem === 'count') {
                problems.push(`${where}: צריך בין 1 ל-${maxVoices} קולות, מופרדים בפסיק.`);
            } else if (problem === 'pattern') {
                problems.push(`${where}: מזהה קול יכול להכיל אותיות באנגלית, ספרות ו-. _ : - בלבד.`);
            }
        }
    }
    return problems;
};

/** true - the user changed something in the form that was not saved */
export const isInworldFormChanged = (form, inworldView) => JSON.stringify(form) !== JSON.stringify(formFromInworldView(inworldView));
